import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType, Table, Pagination } from 'src/app/shared/types';
import { CustomerInvoiceReceiptRequestsService } from './customer-invoice-receipt-requests.service';
import { environment } from 'src/environments/environment';

const DEFAULT_CONSTRAINTS = {
  maxLookbackDays: 365,
  maxRangeDays: 90,
  defaultRangeDays: 7,
};

const MAX_EMAIL_LENGTH = 254;

@Component({
  selector: 'app-customer-invoice-receipt-requests',
  templateUrl: './customer-invoice-receipt-requests.component.html',
  styleUrls: ['./customer-invoice-receipt-requests.component.css']
})
export class CustomerInvoiceReceiptRequestsComponent implements OnInit {
  @Input() customerId: string;
  @Input() customerEmail: string;
  @Input() customerDetailsToggleableHeaders: string[];
  @Output() toggleHeaders: EventEmitter<any> = new EventEmitter();

  headerTable: Table;

  email = '';
  fromDate: Date;
  toDate: Date;

  minFromDate: Date;
  maxFromDate: Date;
  minToDate: Date;
  maxToDate: Date;

  validationError: string = '';
  submitting = false;
  constraints = DEFAULT_CONSTRAINTS;

  constructor(
    private toasterService: ToasterService,
    private invoiceReceiptRequestsService: CustomerInvoiceReceiptRequestsService,
  ) {}

  ngOnInit() {
    let cfg: any = {};
    const envAny: any = environment as any;
    if (envAny && envAny.invoiceReceiptRequest) {
      cfg = envAny.invoiceReceiptRequest;
    }
    this.constraints = {
      maxLookbackDays: Math.max(1, Number(cfg.maxLookbackDays) || DEFAULT_CONSTRAINTS.maxLookbackDays),
      maxRangeDays: Math.max(1, Number(cfg.maxRangeDays) || DEFAULT_CONSTRAINTS.maxRangeDays),
      defaultRangeDays: Math.max(1, Number(cfg.defaultRangeDays) || DEFAULT_CONSTRAINTS.defaultRangeDays),
    };
    this.email = this.customerEmail || '';
    this.initHeader();
    this.initDates();
    this.recomputeBoundsAndValidate();
  }

  changeInHeader(header) {
    this.toggleHeaders.emit(header);
  }

  initHeader() {
    this.headerTable = new Table({
      toggleableHeaders: this.customerDetailsToggleableHeaders,
      selectedHeader: 6,
      headers: {},
      data: [],
      pagination: new Pagination(null, null, null, null, 0, false),
      defaultError: '',
    });
  }

  initDates() {
    const now = new Date();
    const from = new Date(now);
    const defaultRangeDays = Math.min(this.constraints.defaultRangeDays, this.constraints.maxRangeDays);
    from.setDate(from.getDate() - defaultRangeDays);
    this.fromDate = from;
    this.toDate = now;
  }

  onFromDateChange() {
    const prevError = this.validationError;
    this.recomputeBoundsAndValidate();
    if (this.validationError && this.validationError !== prevError) {
      this.toasterService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: `Invalid date range. ${this.validationError}`,
      }));
    }
  }

  onToDateChange() {
    const prevError = this.validationError;
    this.recomputeBoundsAndValidate();
    if (this.validationError && this.validationError !== prevError) {
      this.toasterService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: `Invalid date range. ${this.validationError}`,
      }));
    }
  }

  onEmailChange() {
    this.recomputeBoundsAndValidate();
  }

  private getTodayDateOnly(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private getLookbackMinDate(today: Date): Date {
    const maxLookbackDays = this.constraints.maxLookbackDays;
    const lookback = new Date(today);
    lookback.setDate(lookback.getDate() - (maxLookbackDays - 1));
    return lookback;
  }

  private recomputeBoundsAndValidate() {
    // Use date-only (local start-of-day) for bounds to avoid time-of-day off-by-one issues.
    const today = this.getTodayDateOnly();
    const lookback = this.getLookbackMinDate(today);

    // Restrict selectable dates via calendar bounds (but NEVER auto-modify user-selected dates)
    const DAY_MS = 24 * 60 * 60 * 1000;
    // Inclusive range: From day + (maxRangeDays - 1 days) = maxRangeDays total days
    const maxRangeDays = this.constraints.maxRangeDays;
    const maxRangeMs = (maxRangeDays - 1) * DAY_MS;

    // From date should always be freely selectable in [today-365, today]
    this.minFromDate = lookback;
    this.maxFromDate = today;

    // To date should be selectable only in [fromDate, min(fromDate + 90 days, today)]
    const from = this.fromDate ? new Date(this.fromDate) : null;
    if (from) {
      const maxToByFrom = new Date(from.getTime() + maxRangeMs);
      this.minToDate = from;
      this.maxToDate = new Date(Math.min(today.getTime(), maxToByFrom.getTime()));
    } else {
      // If from isn't selected yet, keep To constrained only by absolute bounds
      this.minToDate = lookback;
      this.maxToDate = today;
    }

    this.validationError = this.getValidationError();
  }

  private getValidationError(): string {
    if (!this.customerId) return 'Customer not loaded yet.';
    if (!this.fromDate || !this.toDate) return 'Please select both From and To dates.';

    const emailRaw = (this.email || '').trim();
    const email = emailRaw ? emailRaw.toLowerCase() : '';
    if (email) {
      if (email.length > MAX_EMAIL_LENGTH) return 'Invalid email.';
      if (email.indexOf('\r') !== -1 || email.indexOf('\n') !== -1) return 'Invalid email.';
      const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!EMAIL_RE.test(email)) return 'Invalid email.';
    }

    // Validate on date-only (local start-of-day)
    const todayDate = this.getTodayDateOnly();
    const today = todayDate.getTime();
    const from = new Date(this.fromDate.getFullYear(), this.fromDate.getMonth(), this.fromDate.getDate()).getTime();
    const to = new Date(this.toDate.getFullYear(), this.toDate.getMonth(), this.toDate.getDate()).getTime();

    if (!Number.isFinite(from) || !Number.isFinite(to)) return 'Invalid dates selected.';
    if (to > today) return 'To date cannot be in the future.';

    const lookback = this.getLookbackMinDate(todayDate);
    if (from < lookback.getTime()) return `From date cannot be older than ${this.constraints.maxLookbackDays} days.`;

    if (from > to) return 'To date must be on/after From date.';

    const maxRangeDays = this.constraints.maxRangeDays;
    // Inclusive range: allow up to maxRangeDays total => to-from <= (maxRangeDays-1) days in ms (when dates are date-only)
    const maxRangeMs = (maxRangeDays - 1) * 24 * 60 * 60 * 1000;
    if ((to - from) > maxRangeMs) return `Requested range cannot exceed ${maxRangeDays} days.`;

    return '';
  }

  async submit() {
    this.recomputeBoundsAndValidate();
    if (this.validationError) {
      this.toasterService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: this.validationError,
      }));
      return;
    }

    const fromD = new Date(this.fromDate.getFullYear(), this.fromDate.getMonth(), this.fromDate.getDate());
    const toD = new Date(this.toDate.getFullYear(), this.toDate.getMonth(), this.toDate.getDate());
    const from = fromD.getTime();
    const to = toD.getTime();
    const emailRaw = (this.email || '').trim();
    const email = emailRaw ? emailRaw.toLowerCase() : '';

    this.submitting = true;
    this.invoiceReceiptRequestsService
      .raiseBulkInvoiceReceiptRequest(this.customerId, from, to, email)
      .subscribe((result: any) => {
        this.submitting = false;
        const message = (result && (result.message || result.metadata || result)) || 'Request submitted.';
        this.toasterService.showToaster(new Toaster({
          type: ToasterType.SUCCESS,
          message: typeof message === 'string' ? message : 'Request submitted.',
        }));
      }, (err) => {
        this.submitting = false;
        this.toasterService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: (err && err.error && err.error.message) || 'Unable to submit request.',
        }));
      });
  }
}


